import { Store, CheckCircle, XCircle, Ban, Mail, Phone, User, Eye, Building2, BadgeCheck } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { STORE_TYPES, STORE_STATUS, BUSINESS_TYPES, getStoreTypeName } from '../../../components/manage-platform/constants';

/**
 * Stores Tab — landing (select type) + per-type list (active/rejected).
 * Wraps two original {activeTab === 'stores' ...} sub-blocks.
 * Extracted from ManagePlatform.js.
 */
export default function StoresTab({
  // State
  activeTab,
  language = 'ar',
  loading,
  storeManageType,
  setStoreManageType,
  storeListFilter,
  setStoreListFilter,
  stores,
  // Helpers from parent
  formatDate,
  getStoreManageCounts,
  getStoresListFiltered,
  // Modal openers / handlers
  setSelectedStore,
  setShowStoreDetails,
}) {
  return (
    <>
            {activeTab === 'stores' && storeManageType === null && (
              <div>
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Store className="h-6 w-6 text-[#c9a227]" />
                    إدارة المتاجر
                  </h2>
                </div>
                
                <p className="text-[#808080] mb-8 text-center">
                  {language === 'ar' ? 'اختر نوع المتاجر للإدارة' : 'Choose a store type to manage'}
                </p>
                
                {/* Main Buttons - شخصي / شركة */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                  {/* زر إدارة متاجر شخصية */}
                  <div
                    onClick={() => {
                      setStoreManageType('personal');
                      setStoreListFilter('active');
                    }}
                    data-testid="manage-stores-personal-btn"
                    className="group bg-[#1a1a2e] rounded-2xl p-8 border border-[#2a2a4e] hover:border-purple-500 transition-all cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:shadow-purple-500/20"
                  >
                    <div className="text-center">
                      <div className="w-24 h-24 bg-gradient-to-br from-purple-500/20 to-purple-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform border border-purple-500/30">
                        <User className="h-12 w-12 text-purple-400" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">
                        {language === 'ar' ? 'إدارة متاجر شخصية' : 'Manage Personal Stores'}
                      </h3>
                      <p className="text-sm text-[#808080] mb-4">
                        {language === 'ar' ? 'المتاجر الشخصية الفعّالة' : 'Approved individual stores'}
                      </p>
                      {getStoreManageCounts().personalCount > 0 && (
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-400 rounded-full text-sm font-medium">
                          <Store className="h-4 w-4" />
                          {getStoreManageCounts().personalCount} {language === 'ar' ? 'متجر' : 'stores'}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* زر إدارة متاجر الشركات */}
                  <div
                    onClick={() => {
                      setStoreManageType('company');
                      setStoreListFilter('active');
                    }}
                    data-testid="manage-stores-company-btn"
                    className="group bg-[#1a1a2e] rounded-2xl p-8 border border-[#2a2a4e] hover:border-teal-500 transition-all cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:shadow-teal-500/20"
                  >
                    <div className="text-center">
                      <div className="w-24 h-24 bg-gradient-to-br from-teal-500/20 to-teal-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform border border-teal-500/30">
                        <Building2 className="h-12 w-12 text-teal-400" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">
                        {language === 'ar' ? 'إدارة متاجر الشركات' : 'Manage Company Stores'}
                      </h3>
                      <p className="text-sm text-[#808080] mb-4">
                        {language === 'ar' ? 'متاجر الشركات الفعّالة' : 'Approved company stores'}
                      </p>
                      {getStoreManageCounts().companyCount > 0 && (
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 text-teal-400 rounded-full text-sm font-medium">
                          <Building2 className="h-4 w-4" />
                          {getStoreManageCounts().companyCount} {language === 'ar' ? 'متجر' : 'stores'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'stores' && storeManageType !== null && (
              <div>
                {/* Header */}
                <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${
                      storeManageType === 'personal' 
                        ? 'bg-purple-500/20 border-purple-500/30' 
                        : 'bg-teal-500/20 border-teal-500/30'
                    }`}>
                      {storeManageType === 'personal' ? (
                        <User className="h-6 w-6 text-purple-400" />
                      ) : (
                        <Building2 className="h-6 w-6 text-teal-400" />
                      )}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">
                        {storeManageType === 'personal'
                          ? (language === 'ar' ? 'إدارة متاجر شخصية' : 'Manage Personal Stores')
                          : (language === 'ar' ? 'إدارة متاجر الشركات' : 'Manage Company Stores')}
                      </h2>
                      <p className="text-sm text-[#808080]">
                        {storeManageType === 'personal' ? 'Individual Stores' : 'Company Stores'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Switch Toggle للتبديل بين المتاجر الفعالة والمرفوضة */}
                  <div className="flex items-center gap-2 bg-[#0a0a0a] p-1 rounded-xl border border-[#2a2a4e]">
                    <button
                      onClick={() => setStoreListFilter('active')}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                        storeListFilter === 'active'
                          ? 'bg-emerald-600 text-white'
                          : 'text-[#808080] hover:text-white'
                      }`}
                    >
                      <CheckCircle className="h-4 w-4" />
                      {language === 'ar' ? 'المتاجر الفعالة' : 'Active Stores'}
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        storeListFilter === 'active' ? 'bg-emerald-800' : 'bg-[#2a2a4e]'
                      }`}>
                        {getStoresListFiltered().filter(s => s.status === 'active' || s.status === 'approved').length || stores.filter(s => {
                          const isPersonal = !s.business_type || s.business_type === 'individual' || s.business_type === 'personal';
                          const matchType = storeManageType === 'personal' ? isPersonal : s.business_type === 'company';
                          return matchType && (s.status === 'active' || s.status === 'approved');
                        }).length}
                      </span>
                    </button>
                    <button
                      onClick={() => setStoreListFilter('stopped')}
                      data-testid="stores-tab-stopped"
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                        storeListFilter === 'stopped'
                          ? 'bg-amber-600 text-white'
                          : 'text-[#808080] hover:text-white'
                      }`}
                    >
                      <Ban className="h-4 w-4" />
                      {language === 'ar' ? 'المتاجر المتوقفة' : 'Stopped Stores'}
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        storeListFilter === 'stopped' ? 'bg-amber-800' : 'bg-[#2a2a4e]'
                      }`}>
                        {stores.filter(s => {
                          const isPersonal = !s.business_type || s.business_type === 'individual' || s.business_type === 'personal';
                          const matchType = storeManageType === 'personal' ? isPersonal : s.business_type === 'company';
                          return matchType && s.status === 'stopped';
                        }).length}
                      </span>
                    </button>
                    <button
                      onClick={() => setStoreListFilter('rejected')}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                        storeListFilter === 'rejected'
                          ? 'bg-red-600 text-white'
                          : 'text-[#808080] hover:text-white'
                      }`}
                    >
                      <XCircle className="h-4 w-4" />
                      {language === 'ar' ? 'المتاجر المرفوضة' : 'Rejected Stores'}
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        storeListFilter === 'rejected' ? 'bg-red-800' : 'bg-[#2a2a4e]'
                      }`}>
                        {stores.filter(s => {
                          const isPersonal = !s.business_type || s.business_type === 'individual' || s.business_type === 'personal';
                          const matchType = storeManageType === 'personal' ? isPersonal : s.business_type === 'company';
                          return matchType && s.status === 'rejected';
                        }).length}
                      </span>
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="space-y-3">
                    {[1,2,3].map(i => <div key={i} className="h-24 bg-[#0a0a0a] rounded-xl animate-pulse" />)}
                  </div>
                ) : getStoresListFiltered().length === 0 ? (
                  <div className="text-center py-16 text-[#808080] bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
                    {storeListFilter === 'active' ? (
                      <>
                        <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50 text-emerald-500" />
                        <p>{language === 'ar' ? 'لا توجد متاجر فعالة حالياً' : 'No active stores'}</p>
                        <p className="text-sm text-[#606060] mt-2">
                          {language === 'ar' ? 'ستظهر هنا المتاجر بعد الموافقة عليها' : 'Approved stores will appear here'}
                        </p>
                      </>
                    ) : storeListFilter === 'stopped' ? (
                      <>
                        <Ban className="h-12 w-12 mx-auto mb-3 opacity-50 text-amber-500" />
                        <p>{language === 'ar' ? 'لا توجد متاجر متوقفة' : 'No stopped stores'}</p>
                        <p className="text-sm text-[#606060] mt-2">
                          {language === 'ar' ? 'يمكن أرشفة المتاجر المتوقفة من هنا' : 'Stopped stores can be archived from here'}
                        </p>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-12 w-12 mx-auto mb-3 opacity-50 text-red-500" />
                        <p>{language === 'ar' ? 'لا توجد متاجر مرفوضة' : 'No rejected stores'}</p>
                        <p className="text-sm text-[#606060] mt-2">
                          {language === 'ar' ? 'ستظهر هنا المتاجر التي تم رفضها' : 'Rejected stores will appear here'}
                        </p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getStoresListFiltered().map((store) => {
                      const isApproved = store.status === 'active' || store.status === 'approved';
                      const isStopped = store.status === 'stopped';
                      const status = STORE_STATUS[store.status] || STORE_STATUS.pending;
                      const StatusIcon = status.icon;
                      const borderClass = isApproved
                        ? 'border-emerald-500/30 hover:border-emerald-500/50'
                        : isStopped
                          ? 'border-amber-500/30 hover:border-amber-500/50'
                          : 'border-red-500/30 hover:border-red-500/50';
                      const iconBg = isApproved
                        ? 'bg-emerald-500/20'
                        : isStopped ? 'bg-amber-500/20' : 'bg-red-500/20';
                      const iconColor = isApproved
                        ? 'text-emerald-500'
                        : isStopped ? 'text-amber-500' : 'text-red-500';
                      return (
                        <div
                          key={store.id}
                          className={`p-5 bg-[#1a1a2e] rounded-2xl border transition-all ${borderClass}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            {/* Store Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-3">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconBg}`}>
                                  <Store className={`h-6 w-6 ${iconColor}`} />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h3 className="font-bold text-white text-lg">{store.name || store.nameEn || 'بدون اسم'}</h3>
                                    {store.verified && <BadgeCheck className="h-5 w-5 text-[#c9a227]" />}
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${status.color}`}>
                                      <StatusIcon className="h-3 w-3" />
                                      {status.label}
                                    </span>
                                  </div>
                                  {store.nameEn && store.name && (
                                    <p className="text-sm text-[#808080]">{store.nameEn}</p>
                                  )}
                                </div>
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                {/* نشاط المتجر */}
                                <div className="bg-[#0a0a0a] rounded-xl p-3">
                                  <p className="text-xs text-[#606060] mb-1">نشاط</p>
                                  <p className="text-sm text-white font-medium">
                                    {getStoreTypeName(store.type, language) || store.type || (language === 'ar' ? 'غير محدد' : 'Not specified')}
                                  </p>
                                </div>
                                
                                {/* نوع الشركة */}
                                <div className="bg-[#0a0a0a] rounded-xl p-3">
                                  <p className="text-xs text-[#606060] mb-1">نوع الشركة</p>
                                  <p className="text-sm text-white font-medium">
                                    {BUSINESS_TYPES[store.business_type] || store.business_type || 'غير محدد'}
                                  </p>
                                </div>
                                
                                {/* المالك */}
                                <div className="bg-[#0a0a0a] rounded-xl p-3">
                                  <p className="text-xs text-[#606060] mb-1">المالك</p>
                                  <p className="text-sm text-white font-medium">{store.owner_name || 'غير محدد'}</p>
                                </div>
                                
                                {/* التاريخ */}
                                <div className="bg-[#0a0a0a] rounded-xl p-3">
                                  <p className="text-xs text-[#606060] mb-1">
                                    {isApproved ? (language === 'ar' ? 'تاريخ التفعيل' : 'Activation date') : (language === 'ar' ? 'تاريخ الرفض' : 'Rejection date')}
                                  </p>
                                  <p className="text-sm text-white font-medium">
                                    {formatDate(store.approved_at || store.rejected_at || store.updated_at)}
                                  </p>
                                </div>
                              </div>

                              {/* Contact Info */}
                              <div className="flex flex-wrap gap-4 text-sm">
                                {store.owner_email && (
                                  <span className="flex items-center gap-1 text-[#808080]">
                                    <Mail className="h-4 w-4" />
                                    {store.owner_email}
                                  </span>
                                )}
                                {store.owner_phone && (
                                  <span className="flex items-center gap-1 text-[#808080]">
                                    <Phone className="h-4 w-4" />
                                    <span dir="ltr">{store.owner_phone}</span>
                                  </span>
                                )}
                              </div>
                              
                              {/* سبب الرفض — backend stores it as reject_reason; older docs may use rejection_reason */}
                              {store.status === 'rejected' && (store.reject_reason || store.rejection_reason) && (
                                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl" data-testid={`reject-reason-${store.id}`}>
                                  <p className="text-xs text-red-400 mb-1">سبب الرفض:</p>
                                  <p className="text-sm text-white">{store.reject_reason || store.rejection_reason}</p>
                                </div>
                              )}
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col gap-2">
                              <Button
                                onClick={() => {
                                  setSelectedStore(store);
                                  setShowStoreDetails(true);
                                }}
                                variant="outline"
                                className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]"
                              >
                                <Eye className="h-4 w-4 me-2" />
                                التفاصيل
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
    </>
  );
}
